(function () {
  "use strict";

  // -------------------------------------------------------------------------
  // Pure engine — no DOM access. Exposed on window.themeVariables.finderEngine
  // so the Jest suite can exercise it directly. The <product-finder> element
  // (below) wires the DOM to these functions.
  // -------------------------------------------------------------------------

  // JS twin of snippets/normalize-text.liquid: lowercase; & -> und; German
  // umlauts/eszett expanded; whitespace (incl. thin/narrow no-break spaces),
  // hyphens/dashes, slashes, zero-width chars and punctuation stripped. Any two
  // labels that normalize to the same token are treated as equal.
  function normalizeText(str) {
    var value = String(str == null ? "" : str).toLowerCase();

    value = value.replace(/&amp;/g, "und").replace(/&/g, "und");
    value = value
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss");

    // Whitespace: regular, thin (U+2009), narrow no-break (U+202F), no-break (U+00A0).
    value = value.replace(/[\s   ]/g, "");
    // Dashes: hyphen-minus plus look-alikes U+2010..U+2015 and minus U+2212.
    value = value.replace(/[-‐‑‒–—―−]/g, "");
    // Slashes and backslash.
    value = value.replace(/[/\\]/g, "");
    // Zero-width: ZWSP U+200B, ZWNJ U+200C, ZWJ U+200D, BOM/ZWNBSP U+FEFF.
    value = value.replace(/[​‌‍﻿]/g, "");
    // Punctuation.
    value = value.replace(/[.,:;!?()[\]'"`’‘„“”…%]/g, "");

    return value;
  }

  // Extract every W x H pair as a canonical "WxH" token (ints, so "090" == "90").
  // Used by the exact-size-list source ('groesse'); kg-only strings contribute nothing.
  function parseSizePairs(values) {
    var pairs = [];

    (values || []).forEach(function (raw) {
      var text = String(raw == null ? "" : raw);
      var pattern = /(\d+)\s*[x×]\s*(\d+)/gi;
      var match;

      while ((match = pattern.exec(text)) !== null) {
        var width = parseInt(match[1], 10);
        var height = parseInt(match[2], 10);

        if (!isNaN(width) && !isNaN(height)) {
          pairs.push(width + "x" + height);
        }
      }
    });

    return pairs;
  }

  function findStep(config, stepId) {
    for (var i = 0; i < config.steps.length; i += 1) {
      if (config.steps[i].stepId === stepId) {
        return config.steps[i];
      }
    }

    return null;
  }

  function stepIndex(config, stepId) {
    for (var i = 0; i < config.steps.length; i += 1) {
      if (config.steps[i].stepId === stepId) {
        return i;
      }
    }

    return -1;
  }

  function findAnswer(step, answerId) {
    if (!step) {
      return null;
    }

    for (var i = 0; i < step.answers.length; i += 1) {
      if (step.answers[i].id === answerId) {
        return step.answers[i];
      }
    }

    return null;
  }

  function selectedAnswers(selections, step) {
    var ids = (selections && selections[step.stepId]) || [];

    return ids
      .map(function (id) {
        return findAnswer(step, id);
      })
      .filter(Boolean);
  }

  // The applicable-step set opened by a single first-step answer. Returns an
  // array of stepIds from the branch target onward in document order, or [] for
  // a terminal (jump === 'results') branch.
  function branchApplicableSteps(config, answer) {
    if (!answer || answer.jump === "results") {
      return [];
    }

    var firstIndex = 0;
    var targetIndex;

    if (
      answer.jump &&
      answer.jump !== "next" &&
      findStep(config, answer.jump)
    ) {
      targetIndex = stepIndex(config, answer.jump);
    } else {
      // 'next' or an unknown id -> the step after the first in document order.
      targetIndex = firstIndex + 1;
    }

    if (targetIndex < 0 || targetIndex >= config.steps.length) {
      return [];
    }

    var steps = [];
    var skipped = answer.skipSteps || [];

    for (var i = targetIndex; i < config.steps.length; i += 1) {
      if (skipped.indexOf(config.steps[i].stepId) === -1) {
        steps.push(config.steps[i].stepId);
      }
    }

    return steps;
  }

  // Branch descriptors for the selected first-step answers, in config answer order.
  function branchesFor(selections, config) {
    var firstStep = config.steps[0];
    var firstIds = (selections && selections[firstStep.stepId]) || [];

    return firstStep.answers
      .filter(function (answer) {
        return firstIds.indexOf(answer.id) !== -1;
      })
      .map(function (answer) {
        return {
          answer: answer,
          applicable: branchApplicableSteps(config, answer),
        };
      });
  }

  function computePath(selections, config) {
    var firstStep = config.steps[0];
    var path = [firstStep.stepId];
    var branches = branchesFor(selections, config);

    // Nothing selected on the first step yet -> assume the full document-order
    // flow, so the progress track shows all expected segments upfront (Figma
    // 7001-6236). The path recomputes as soon as a branch is opened.
    if (!branches.length) {
      return {
        steps: config.steps.map(function (step) {
          return step.stepId;
        }),
        terminal: false,
      };
    }

    // Union the applicable sets, preserving document order.
    var union = [];

    config.steps.forEach(function (step, idx) {
      if (idx === 0) {
        return;
      }

      var inSomeBranch = branches.some(function (branch) {
        return branch.applicable.indexOf(step.stepId) !== -1;
      });

      if (inSomeBranch) {
        union.push(step.stepId);
      }
    });

    // terminal when every branch is terminal (empty union): next action is results.
    if (!union.length) {
      return { steps: path, terminal: true };
    }

    path = path.concat(union);

    // For steps after the first: an answered step whose selected answers ALL
    // have jump === 'results' truncates the path after that step. Step-id jumps
    // on non-first steps are treated as 'next' (never a jump target), so only
    // 'results' truncates here.
    for (var i = 1; i < path.length; i += 1) {
      var step = findStep(config, path[i]);
      var chosen = selectedAnswers(selections, step);

      if (
        chosen.length &&
        chosen.every(function (answer) {
          return answer.jump === "results";
        })
      ) {
        path = path.slice(0, i + 1);
        break;
      }
    }

    return { steps: path, terminal: false };
  }

  // An answer with a visibleIf list is only offered (and only keeps its
  // selection) while at least one of the referenced answer ids is selected —
  // e.g. the chart's "Für Allergiker: only visible if Bettdecken chosen".
  function isAnswerVisible(answer, selections) {
    if (!answer || !answer.visibleIf || !answer.visibleIf.length) {
      return true;
    }

    return answer.visibleIf.some(function (requiredId) {
      return Object.keys(selections || {}).some(function (stepId) {
        return (selections[stepId] || []).indexOf(requiredId) !== -1;
      });
    });
  }

  function pruneSelections(selections, config) {
    var path = computePath(selections, config).steps;
    var next = {};

    path.forEach(function (stepId) {
      var ids = (selections && selections[stepId]) || [];
      var step = findStep(config, stepId);

      if (!step) {
        return;
      }

      var kept = ids.filter(function (id) {
        var answer = findAnswer(step, id);

        return Boolean(answer) && isAnswerVisible(answer, selections);
      });

      if (kept.length) {
        next[stepId] = kept;
      }
    });

    return next;
  }

  function matchesAnswer(product, answer) {
    switch (answer.source) {
      case "keine-praeferenz":
        return true;

      case "produktkategorie":
        return anyValueMatches(product.kategorie, answer.values);
      case "nutzungszweck":
        return anyValueMatches(product.zweck, answer.values);
      case "material":
        return anyValueMatches(product.material, answer.values);
      case "waermeklasse":
        return anyValueMatches(product.waerme, answer.values);
      case "tag":
        return anyValueMatches(product.tags, answer.values);

      case "collection":
        // Collection handles compare raw (not normalized).
        return (product.collections || []).indexOf(answer.collection) !== -1;

      case "groesse":
        return matchesSize(product, answer);

      case "variant-color":
        return matchesColor(product, answer);

      default:
        return false;
    }
  }

  function anyValueMatches(productValues, answerValues) {
    var normalizedAnswers = (answerValues || []).map(normalizeText);

    return (productValues || []).some(function (productValue) {
      return normalizedAnswers.indexOf(normalizeText(productValue)) !== -1;
    });
  }

  // One size-answer check over a raw value list. 'groesse' matches exact W x H
  // pairs from the answer's configured values.
  function matchesSizeValues(rawValues, answer) {
    var productPairs = parseSizePairs(rawValues);
    var answerPairs = parseSizePairs(answer.values);

    return productPairs.some(function (pair) {
      return answerPairs.indexOf(pair) !== -1;
    });
  }

  function matchesSize(product, answer) {
    var primaryHasData = parseSizePairs(product.groese).length;

    if (primaryHasData) {
      return matchesSizeValues(product.groese, answer);
    }

    return matchesSizeValues(
      (product.variants || []).map(function (variant) {
        return variant.size;
      }),
      answer
    );
  }

  function matchesColor(product, answer) {
    var normalizedValues = (answer.values || []).map(normalizeText);
    var variantColors = (product.variants || [])
      .map(function (variant) {
        return variant.color;
      })
      .filter(function (color) {
        return color;
      });

    if (variantColors.length) {
      return variantColors.some(function (color) {
        return normalizedValues.indexOf(normalizeText(color)) !== -1;
      });
    }

    // No variant carries a color -> fall back to the farbe metafield.
    return (product.farbe || []).some(function (farbe) {
      return normalizedValues.indexOf(normalizeText(farbe)) !== -1;
    });
  }

  // Does a product satisfy one branch? It must match the branch's first-step
  // answer AND, for every LATER answered step in the branch's applicable set,
  // match >=1 selected answer — unless a selected answer there is
  // keine-praeferenz (which leaves the step unconstrained). Missing data = no match.
  function matchesBranch(product, branch, selections, config) {
    if (!matchesAnswer(product, branch.answer)) {
      return false;
    }

    for (var i = 0; i < branch.applicable.length; i += 1) {
      var stepId = branch.applicable[i];
      var step = findStep(config, stepId);
      var chosen = selectedAnswers(selections, step);

      if (!chosen.length) {
        continue;
      }

      var unconstrained = chosen.some(function (answer) {
        return answer.source === "keine-praeferenz";
      });

      if (unconstrained) {
        continue;
      }

      var matchesStep = chosen.some(function (answer) {
        return matchesAnswer(product, answer);
      });

      if (!matchesStep) {
        return false;
      }
    }

    return true;
  }

  function filterIndex(index, selections, config, max) {
    var limit = typeof max === "number" ? max : 4;
    var branches = branchesFor(selections, config);

    if (!branches.length) {
      return { products: [], totalMatched: 0 };
    }

    // Per-branch matched products, in index (best-selling) order.
    var perBranch = branches.map(function (branch) {
      return index.filter(function (product) {
        return matchesBranch(product, branch, selections, config);
      });
    });

    // Union deduped by handle, preserving index order.
    var seen = {};
    var union = [];

    index.forEach(function (product) {
      var inAny = perBranch.some(function (list) {
        return list.indexOf(product) !== -1;
      });

      if (inAny && !seen[product.handle]) {
        seen[product.handle] = true;
        union.push(product);
      }
    });

    var totalMatched = union.length;

    if (totalMatched <= limit) {
      return { products: union, totalMatched: totalMatched };
    }

    var branchesWithResults = perBranch.filter(function (list) {
      return list.length > 0;
    });

    if (branchesWithResults.length > 1) {
      // Round-robin across branches (branch order = order of selected first-step
      // answers in config), skipping duplicates, until max.
      var result = [];
      var picked = {};
      var cursors = perBranch.map(function () {
        return 0;
      });
      var exhausted = false;

      while (result.length < limit && !exhausted) {
        exhausted = true;

        for (var b = 0; b < perBranch.length && result.length < limit; b += 1) {
          var list = perBranch[b];

          while (cursors[b] < list.length && picked[list[cursors[b]].handle]) {
            cursors[b] += 1;
          }

          if (cursors[b] < list.length) {
            var product = list[cursors[b]];
            cursors[b] += 1;
            picked[product.handle] = true;
            result.push(product);
            exhausted = false;
          }
        }
      }

      return { products: result, totalMatched: totalMatched };
    }

    // Single producing branch (or fewer) -> first `max` in index order.
    return { products: union.slice(0, limit), totalMatched: totalMatched };
  }

  // Resolve the color constraint from the answered variant-color step (ignoring
  // keine-praeferenz). Returns an array of normalized color values, or null when
  // there is no color constraint.
  function colorConstraint(selections, config) {
    for (var i = 0; i < config.steps.length; i += 1) {
      var step = config.steps[i];
      var chosen = selectedAnswers(selections, step);

      if (!chosen.length) {
        continue;
      }

      var isColorStep =
        chosen.every(function (answer) {
          return (
            answer.source === "variant-color" ||
            answer.source === "keine-praeferenz"
          );
        }) &&
        chosen.some(function (answer) {
          return answer.source === "variant-color";
        });

      if (!isColorStep) {
        continue;
      }

      if (
        chosen.some(function (answer) {
          return answer.source === "keine-praeferenz";
        })
      ) {
        return null;
      }

      var values = [];

      chosen.forEach(function (answer) {
        (answer.values || []).forEach(function (value) {
          values.push(normalizeText(value));
        });
      });

      return values;
    }

    return null;
  }

  // The answered size answer (either source), used to prefer a size-matching
  // variant in pickVariant. Returns the answer object or null.
  function sizeConstraint(selections, config) {
    for (var i = 0; i < config.steps.length; i += 1) {
      var step = config.steps[i];
      var chosen = selectedAnswers(selections, step);

      if (!chosen.length) {
        continue;
      }

      var sizeAnswer = null;

      chosen.forEach(function (answer) {
        if (answer.source === "groesse") {
          sizeAnswer = answer;
        }
      });

      if (sizeAnswer) {
        return sizeAnswer;
      }
    }

    return null;
  }

  function pickVariant(product, selections, config) {
    var colors = colorConstraint(selections, config);

    if (!colors) {
      return null;
    }

    var size = sizeConstraint(selections, config);
    var variants = product.variants || [];
    var available = variants.filter(function (variant) {
      return variant.available;
    });

    function colorMatches(variant) {
      return (
        variant.color && colors.indexOf(normalizeText(variant.color)) !== -1
      );
    }

    function colorAndSizeMatch(variant) {
      return (
        colorMatches(variant) && (size ? variantSizeMatches(variant) : true)
      );
    }

    function variantSizeMatches(variant) {
      return matchesSizeValues([variant.size], size);
    }

    // Only pick a variant when the product matches the colour constraint via a
    // variant colour. A product matched purely through the farbe metafield (no
    // variant carries a colour) has no variant to return.
    var variantLevelMatch = variants.some(colorMatches);

    if (!variantLevelMatch) {
      return null;
    }

    // Preference order: available colour+size, available colour, any colour
    // match even if unavailable (still preferring one that also matches size),
    // then first available candidate. A colour-matching variant must win over
    // an available wrong-colour one — otherwise the comparison column would be
    // mislabeled.
    var tiers = [
      available.filter(colorAndSizeMatch),
      available.filter(colorMatches),
      variants.filter(colorAndSizeMatch),
      variants.filter(colorMatches),
      available,
    ];

    for (var i = 0; i < tiers.length; i += 1) {
      if (tiers[i].length) {
        return tiers[i][0].id;
      }
    }

    return null;
  }

  function buildResultsQuery(picked) {
    return (picked || [])
      .map(function (entry) {
        if (entry.variantId == null) {
          return entry.handle;
        }

        return entry.handle + ":" + entry.variantId;
      })
      .join(",");
  }

  function buildResultsUrl(baseUrl, picked) {
    var params = new URLSearchParams();
    var products = buildResultsQuery(picked);

    if (products) {
      params.set("products", products);
    }

    params.set("source", "finder");

    params.set("view", "produktvergleich");

    return baseUrl + "?" + params.toString();
  }

  var engine = {
    normalizeText: normalizeText,
    parseSizePairs: parseSizePairs,
    computePath: computePath,
    branchesFor: branchesFor,
    pruneSelections: pruneSelections,
    isAnswerVisible: isAnswerVisible,
    matchesAnswer: matchesAnswer,
    filterIndex: filterIndex,
    pickVariant: pickVariant,
    buildResultsQuery: buildResultsQuery,
    buildResultsUrl: buildResultsUrl,
  };

  window.themeVariables = window.themeVariables || {};
  window.themeVariables.finderEngine =
    window.themeVariables.finderEngine || engine;

  // -------------------------------------------------------------------------
  // <product-finder> custom element — wires the DOM to the pure engine.
  // -------------------------------------------------------------------------

  if (window.customElements.get("product-finder")) {
    return;
  }

  var STORAGE_KEY = "rd:finder:v1";
  var URL_PARAM = "pf";
  var AUTO_ADVANCE_MS = 350;

  class ProductFinder extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      var signal = this._abortController.signal;

      this._stepsRoot = this.querySelector("[data-finder-steps]");
      this._actions = this.querySelector("[data-finder-actions]");
      this._nextButton = this.querySelector("[data-finder-next]");
      this._backButton = this.querySelector("[data-finder-back]");
      this._status = this.querySelector("[data-finder-status]");
      this._statusText = this.querySelector("[data-finder-status-text]");
      this._errorLink = this.querySelector("[data-finder-error-link]");

      this._progressRoot = this.querySelector("[data-finder-progress]");
      this._progressLabels = this.querySelector("[data-progress-labels]");
      this._progressTrack = this.querySelector("[data-progress-track]");
      this._progressCurrent = this.querySelector("[data-progress-current]");
      this._progressPrev = this.querySelector("[data-progress-prev]");
      this._progressNext = this.querySelector("[data-progress-next-arrow]");

      this._maxResults = parseInt(this.dataset.maxResults, 10) || 4;
      this._trackingEnabled = this.dataset.enableTracking === "true";

      this._config = this._readConfig();
      this._questionSections = {};

      this.querySelectorAll("section[data-finder-question]").forEach(
        function (section) {
          this._questionSections[section.dataset.stepId] = section;
        }.bind(this)
      );

      this._indexPromise = null;

      this._selections = this._pruneToConfig(this._restoreSelections());
      var path = engine.computePath(this._selections, this._config).steps;
      var savedCurrent = this._restoreCurrentStep();
      this._currentStep =
        savedCurrent && path.indexOf(savedCurrent) !== -1
          ? savedCurrent
          : this._firstUnansweredStep(path);

      this._forwardButton = this.querySelector("[data-finder-forward]");
      this._ctaBar = this.querySelector("[data-finder-cta]");

      this._nextButton.addEventListener(
        "click",
        function () {
          this._onNext();
        }.bind(this),
        { signal }
      );
      this._backButton.addEventListener(
        "click",
        function () {
          this._onBack();
        }.bind(this),
        { signal }
      );

      if (this._forwardButton) {
        this._forwardButton.addEventListener(
          "click",
          function () {
            this._onProgressNext();
          }.bind(this),
          { signal }
        );
      }

      // Both reset buttons (mobile footer + desktop progress row) share the handler.
      this.querySelectorAll("[data-finder-reset]").forEach(
        function (button) {
          button.addEventListener(
            "click",
            function () {
              this._onReset();
            }.bind(this),
            { signal }
          );
        }.bind(this)
      );

      this.addEventListener(
        "click",
        function (event) {
          var progressStep = event.target.closest("button[data-progress-step]");

          if (progressStep && this.contains(progressStep)) {
            var progressStepId = progressStep.dataset.progressStep;
            var progressPath = engine.computePath(
              this._selections,
              this._config
            ).steps;
            var progressIndex = progressPath.indexOf(progressStepId);
            var currentIndex = progressPath.indexOf(this._currentStep);

            if (progressIndex >= 0 && progressIndex < currentIndex) {
              this._goTo(progressStepId);
              document.dispatchEvent(
                new CustomEvent("rd:finder:back", {
                  detail: { stepId: progressStepId },
                })
              );
            }

            return;
          }

          var tile = event.target.closest("[data-finder-answer]");

          if (tile && this.contains(tile)) {
            this._onTileClick(tile);
          }
        }.bind(this),
        { signal }
      );

      if (this._progressPrev) {
        this._progressPrev.addEventListener(
          "click",
          function () {
            this._onBack();
          }.bind(this),
          { signal }
        );
      }

      if (this._progressNext) {
        this._progressNext.addEventListener(
          "click",
          function () {
            this._onProgressNext();
          }.bind(this),
          { signal }
        );
      }

      // Browser back can restore this page from the back/forward cache with
      // the exact DOM state it had while results were loading. Clear that
      // transient state so the visitor can submit the restored quiz again.
      window.addEventListener(
        "pageshow",
        function (event) {
          if (event.persisted) {
            this._clearStatus();
            this._nextButton.disabled = false;
          }
        }.bind(this),
        { signal }
      );

      this._update({ focus: false });
      this._ensureIndex();
    }

    disconnectedCallback() {
      if (this._abortController) {
        this._abortController.abort();
      }

      clearTimeout(this._advanceTimer);
    }

    // ----- configuration -----

    _readConfig() {
      var steps = [];

      this.querySelectorAll("section[data-finder-question]").forEach(function (
        section
      ) {
        var answers = [];

        section
          .querySelectorAll("[data-finder-answer]")
          .forEach(function (tile) {
            answers.push({
              id: tile.dataset.answerId,
              source: tile.dataset.source,
              values: (tile.dataset.values || "")
                .split("|")
                .filter(function (value) {
                  return value !== "";
                }),
              collection: tile.dataset.collection || "",
              visibleIf: (tile.dataset.visibleIf || "")
                .split(",")
                .map(function (id) {
                  return id.trim();
                })
                .filter(Boolean),
              skipSteps: (tile.dataset.skipSteps || "")
                .split(",")
                .map(function (id) {
                  return id.trim();
                })
                .filter(Boolean),
              jump: tile.dataset.jump || "next",
            });
          });

        steps.push({
          stepId: section.dataset.stepId,
          select: section.dataset.select || "single",
          progressLabel: section.dataset.progressLabel || "",
          answers: answers,
        });
      });

      return { steps: steps };
    }

    _tileFor(stepId, answerId) {
      var section = this._questionSections[stepId];

      if (!section) {
        return null;
      }

      return section.querySelector(
        '[data-finder-answer][data-answer-id="' + answerId + '"]'
      );
    }

    _stepConfig(stepId) {
      return (
        this._config.steps.filter(function (step) {
          return step.stepId === stepId;
        })[0] || null
      );
    }

    _answerConfig(stepId, answerId) {
      var step = this._stepConfig(stepId);

      if (!step) {
        return null;
      }

      return (
        step.answers.filter(function (answer) {
          return answer.id === answerId;
        })[0] || null
      );
    }

    _firstUnansweredStep(path) {
      for (var i = 0; i < path.length; i += 1) {
        var ids = this._selections[path[i]] || [];

        if (!ids.length) {
          return path[i];
        }
      }

      return path[path.length - 1];
    }

    // ----- persistence -----

    _restoreSelections() {
      var params = new URLSearchParams(window.location.search);
      var raw = params.get(URL_PARAM);

      if (raw) {
        var parsed = this._parseCompact(raw);

        if (parsed) {
          return parsed;
        }
      }

      // Stored progress is resumed only on an explicit restore entry — the
      // comparison page's "Zurück zum Quiz" link carries ?restore=1. Every
      // other pf-less entry (search CTA, direct link) starts a fresh quiz.
      if (params.get("restore") != null) {
        try {
          var stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY));

          if (stored && stored.selections) {
            return stored.selections;
          }
        } catch (error) {
          /* corrupted or unavailable storage falls back to empty */
        }

        return {};
      }

      try {
        window.sessionStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        /* ignore */
      }

      return {};
    }

    _restoreCurrentStep() {
      try {
        var stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY));

        if (stored && stored.current) {
          return stored.current;
        }
      } catch (error) {
        /* ignore */
      }

      return null;
    }

    _parseCompact(raw) {
      try {
        var decoded = decodeURIComponent(raw);
        var selections = {};

        decoded.split("~").forEach(function (chunk) {
          if (!chunk) {
            return;
          }

          var parts = chunk.split(":");
          var stepId = parts[0];
          var ids = (parts[1] || "").split(".").filter(Boolean);

          if (stepId && ids.length) {
            selections[stepId] = ids;
          }
        });

        return selections;
      } catch (error) {
        return null;
      }
    }

    _buildCompact() {
      var selections = this._selections;

      return Object.keys(selections)
        .filter(function (stepId) {
          return selections[stepId] && selections[stepId].length;
        })
        .map(function (stepId) {
          return stepId + ":" + selections[stepId].join(".");
        })
        .join("~");
    }

    _pruneToConfig(selections) {
      return engine.pruneSelections(selections, this._config);
    }

    // Gated dataLayer analytics — the rd:finder:* CustomEvents stay ungated.
    _track(eventName, detail) {
      if (!this._trackingEnabled) {
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, detail));
    }

    // Hide the (possibly stale) loading/error status once the quiz moves on.
    _clearStatus() {
      this._status.hidden = true;
      this._errorLink.hidden = true;
    }

    _persist() {
      try {
        window.sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            selections: this._selections,
            current: this._currentStep,
          })
        );
      } catch (error) {
        /* storage unavailable — UI keeps working for this page only */
      }

      var params = new URLSearchParams(window.location.search);
      var compact = this._buildCompact();

      if (compact) {
        params.set(URL_PARAM, encodeURIComponent(compact));
      } else {
        params.delete(URL_PARAM);
      }

      var query = params.toString();

      try {
        window.history.replaceState(
          {},
          "",
          window.location.pathname +
            (query ? "?" + query : "") +
            window.location.hash
        );
      } catch (error) {
        /* replaceState may throw in restricted contexts — ignore */
      }
    }

    // ----- selection handling -----

    _onTileClick(tile) {
      clearTimeout(this._advanceTimer);
      this._clearStatus();

      var section = tile.closest("section[data-finder-question]");
      var stepId = section.dataset.stepId;
      var answerId = tile.dataset.answerId;
      var mode = section.dataset.select || "single";
      var current = this._selections[stepId] || [];

      if (mode === "multi") {
        if (current.indexOf(answerId) !== -1) {
          this._selections[stepId] = current.filter(function (id) {
            return id !== answerId;
          });
        } else {
          this._selections[stepId] = current.concat([answerId]);
        }

        if (!this._selections[stepId].length) {
          delete this._selections[stepId];
        }
      } else {
        this._selections[stepId] = [answerId];
      }

      this._afterSelectionChange(stepId);

      // Single-select auto-advance when the next target is a question.
      if (
        mode === "single" &&
        this._selections[stepId] &&
        this._selections[stepId].length
      ) {
        var target = this._targetFor(stepId);

        if (target && target !== "results") {
          this._advanceTimer = setTimeout(
            function () {
              this._goTo(target);
            }.bind(this),
            AUTO_ADVANCE_MS
          );
        }
      }
    }

    _afterSelectionChange(stepId) {
      this._selections = this._pruneToConfig(this._selections);

      var path = engine.computePath(this._selections, this._config).steps;

      if (path.indexOf(this._currentStep) === -1) {
        this._currentStep = this._firstUnansweredStep(path);
      }

      this._persist();
      this._update({ focus: false });

      var ids = this._selections[stepId] || [];
      document.dispatchEvent(
        new CustomEvent("rd:finder:answer", {
          detail: {
            stepId: stepId,
            answerIds: ids.slice(),
            path: path.slice(),
          },
        })
      );
      this._track("finder_answer", {
        stepId: stepId,
        answerIds: ids.slice(),
        path: path.slice(),
      });
    }

    // The next navigation target after answering a step: a later path step id,
    // or 'results' when this step's selected answer(s) lead to results / there
    // is no following path step.
    _targetFor(stepId) {
      var pathResult = engine.computePath(this._selections, this._config);
      var path = pathResult.steps;
      var index = path.indexOf(stepId);

      if (index === -1) {
        return "results";
      }

      // First step with a terminal path -> results.
      if (index === 0 && pathResult.terminal) {
        return "results";
      }

      var step = this._stepConfig(stepId);
      var ids = this._selections[stepId] || [];
      var chosen = ids
        .map(function (id) {
          return step.answers.filter(function (answer) {
            return answer.id === id;
          })[0];
        })
        .filter(Boolean);

      // A step whose selected answers all point at results ends the quiz.
      if (
        chosen.length &&
        chosen.every(function (answer) {
          return answer.jump === "results";
        })
      ) {
        return "results";
      }

      if (index + 1 < path.length) {
        return path[index + 1];
      }

      return "results";
    }

    _onNext() {
      if (this._targetFor(this._currentStep) === "results") {
        this._showResults();
        return;
      }

      this._goTo(this._targetFor(this._currentStep));
    }

    _onBack() {
      var path = engine.computePath(this._selections, this._config).steps;
      var index = path.indexOf(this._currentStep);

      if (index > 0) {
        this._goTo(path[index - 1]);
        document.dispatchEvent(
          new CustomEvent("rd:finder:back", {
            detail: { stepId: path[index - 1] },
          })
        );
      }
    }

    _onProgressNext() {
      var target = this._targetFor(this._currentStep);

      if (target && target !== "results") {
        this._goTo(target);
      }
    }

    _onReset() {
      clearTimeout(this._advanceTimer);
      this._clearStatus();
      this._selections = {};

      try {
        window.sessionStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        /* ignore */
      }

      var params = new URLSearchParams(window.location.search);
      params.delete(URL_PARAM);
      var query = params.toString();

      try {
        window.history.replaceState(
          {},
          "",
          window.location.pathname +
            (query ? "?" + query : "") +
            window.location.hash
        );
      } catch (error) {
        /* ignore */
      }

      this._currentStep = this._config.steps[0].stepId;
      this._update({ focus: true });
      document.dispatchEvent(
        new CustomEvent("rd:finder:reset", { detail: {} })
      );
      this._track("finder_reset", {});
    }

    _goTo(stepId) {
      clearTimeout(this._advanceTimer);
      this._clearStatus();
      this._currentStep = stepId;
      this._persist();
      this._update({ focus: true });
    }

    // ----- rendering -----

    _update(options) {
      var opts = options || {};
      var path = engine.computePath(this._selections, this._config).steps;

      // Toggle question sections.
      var self = this;
      Object.keys(this._questionSections).forEach(function (id) {
        self._questionSections[id].hidden = id !== self._currentStep;
      });

      // Reflect tile state via aria-pressed + check; hide conditional tiles
      // whose visible-if answers are not selected.
      this.querySelectorAll("[data-finder-answer]").forEach(function (tile) {
        var section = tile.closest("section[data-finder-question]");
        var ids = self._selections[section.dataset.stepId] || [];
        var pressed = ids.indexOf(tile.dataset.answerId) !== -1;
        var answer = self._answerConfig(
          section.dataset.stepId,
          tile.dataset.answerId
        );

        tile.hidden = !engine.isAnswerVisible(answer, self._selections);
        tile.setAttribute("aria-pressed", pressed ? "true" : "false");

        var check = tile.querySelector(".finder-tile__check");

        if (check) {
          check.hidden = !pressed;
        }
      });

      // Back button: hidden on the first path step.
      var currentIndex = path.indexOf(this._currentStep);
      this._backButton.hidden = currentIndex <= 0;

      // "Zurücksetzen" only exists once there is something to reset (Figma 7001-6236).
      var hasAnswers = Object.keys(this._selections).some(function (stepId) {
        return (self._selections[stepId] || []).length > 0;
      });

      this.querySelectorAll("[data-finder-reset]").forEach(function (button) {
        button.hidden = !hasAnswers;
      });

      // Primary button label + visibility.
      this._renderPrimaryButton(path);
      this._renderProgress(path);

      // The sticky CTA bar exists only while the primary button does — otherwise
      // it would paint an empty white strip.
      if (this._ctaBar) {
        this._ctaBar.hidden = this._nextButton.hidden;
      }

      // Hide the footer/status block when nothing inside is visible (untouched
      // first question).
      var footerReset = this._actions.querySelector("[data-finder-reset]");
      var anyActionVisible =
        !this._backButton.hidden ||
        Boolean(footerReset && !footerReset.hidden) ||
        Boolean(this._forwardButton && !this._forwardButton.hidden) ||
        !this._status.hidden;

      this._actions.hidden = !anyActionVisible;

      if (opts.focus) {
        this._focusCurrent();
      }
    }

    _renderPrimaryButton(path) {
      var stepId = this._currentStep;
      var step = this._stepConfig(stepId);
      var ids = this._selections[stepId] || [];
      var answered = ids.length > 0;
      var target = this._targetFor(stepId);
      var isMulti = Boolean(step) && step.select === "multi";

      // Per requirements: single-select steps auto-advance and never show a
      // button; the primary button exists only on multi steps ("Weiter") and
      // whenever the computed target is the results page ("Ergebnis anzeigen").
      this._nextButton.hidden = !answered || !(isMulti || target === "results");

      if (target === "results") {
        this._nextButton.textContent = this.dataset.labelResult || "";
      } else {
        this._nextButton.textContent = this.dataset.labelNext || "";
      }
    }

    _renderProgress(path) {
      var self = this;
      var currentIndex = path.indexOf(this._currentStep);

      // Desktop labels — per Figma only completed steps and the current step
      // carry a label; upcoming steps show as unfilled track segments only.
      if (this._progressLabels) {
        var labels = [];

        path.forEach(function (stepId, index) {
          var step = self._stepConfig(stepId);
          var answered = (self._selections[stepId] || []).length > 0;
          var isCurrent = index === currentIndex;

          if (!answered && !isCurrent) {
            return;
          }

          var canGoBack = answered && index < currentIndex;
          var labelElement = document.createElement(
            canGoBack ? "button" : "span"
          );
          labelElement.className = "finder-progress__label";
          labelElement.dataset.progressStep = stepId;

          if (canGoBack) {
            labelElement.type = "button";
          }

          // Any answered step that is not the current one keeps its checkmark —
          // including completed steps AFTER the current one when revisiting.
          if (answered && !isCurrent) {
            labelElement.classList.add("is-done");

            var check = document.createElement("span");
            check.className = "finder-progress__check";
            check.setAttribute("aria-hidden", "true");
            check.innerHTML =
              '<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 5.5 3.8 8.5 9 1.5" stroke="currentColor"/></svg>';
            labelElement.appendChild(check);

            labelElement.setAttribute(
              "aria-label",
              (step ? step.progressLabel : "") +
                ", " +
                (self.dataset.labelStepDone || "")
            );
          }

          if (isCurrent) {
            labelElement.classList.add("is-current");
          }

          labelElement.appendChild(
            document.createTextNode(step ? step.progressLabel : "")
          );
          labels.push(labelElement);
        });

        this._progressLabels.replaceChildren.apply(
          this._progressLabels,
          labels
        );
      }

      // Desktop track segments.
      if (this._progressTrack) {
        var segments = path.map(function (stepId, index) {
          var segment = document.createElement("span");
          segment.className = "finder-progress__segment";

          if (index <= currentIndex) {
            segment.classList.add("is-filled");
          }

          return segment;
        });

        this._progressTrack.replaceChildren.apply(
          this._progressTrack,
          segments
        );
      }

      // Mobile current-step label + aria.
      if (this._progressCurrent) {
        var currentStep = this._stepConfig(this._currentStep);
        this._progressCurrent.textContent = currentStep
          ? currentStep.progressLabel
          : "";

        var template = this.dataset.progressTemplate || "";
        var aria = template
          .replace("[[current]]", String(currentIndex + 1))
          .replace("[[total]]", String(path.length));
        this._progressCurrent.setAttribute("aria-label", aria);
      }

      // Mobile arrows.
      if (this._progressPrev) {
        this._progressPrev.hidden = currentIndex <= 0;
      }

      var answered = (this._selections[this._currentStep] || []).length > 0;
      var hasNext = currentIndex + 1 < path.length;

      if (this._progressNext) {
        this._progressNext.hidden = !(hasNext && answered);
      }

      // Footer "Weiter ›" link: forward navigation for revisited single-select
      // steps, which have no primary button (multi steps already show one).
      if (this._forwardButton) {
        var currentStepConfig = this._stepConfig(this._currentStep);
        var isMultiStep =
          Boolean(currentStepConfig) && currentStepConfig.select === "multi";
        this._forwardButton.hidden = !(hasNext && answered && !isMultiStep);
      }
    }

    _focusCurrent() {
      var section = this._questionSections[this._currentStep];

      if (!section) {
        return;
      }

      var heading =
        section.querySelector(".product-finder__heading") || section;

      if (heading && typeof heading.focus === "function") {
        if (!heading.hasAttribute("tabindex")) {
          heading.setAttribute("tabindex", "-1");
        }

        heading.focus();
      }
    }

    // ----- index + results -----

    _ensureIndex() {
      if (this._indexPromise) {
        return this._indexPromise;
      }

      var self = this;
      // data-index-url is /collections/all literally — never prepend locale.
      var base = this.dataset.indexUrl;
      var sectionId = this.dataset.indexSectionId;

      function fetchPage(page) {
        var url =
          base +
          "?section_id=" +
          encodeURIComponent(sectionId) +
          "&sort_by=best-selling&page=" +
          page;

        return fetch(url)
          .then(function (response) {
            if (!response.ok) {
              throw new Error("index fetch failed");
            }

            return response.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, "text/html");
            var script = doc.querySelector("[data-finder-index]");

            if (!script) {
              throw new Error("index payload missing");
            }

            var data = JSON.parse(script.textContent);
            var totalPages = parseInt(
              script.getAttribute("data-total-pages"),
              10
            );

            return {
              products: Array.isArray(data) ? data : [],
              totalPages: totalPages >= 1 ? totalPages : 1,
            };
          });
      }

      this._indexPromise = fetchPage(1)
        .then(function (first) {
          if (first.totalPages <= 1) {
            return first.products;
          }

          var rest = [];

          for (var page = 2; page <= first.totalPages; page += 1) {
            rest.push(fetchPage(page));
          }

          return Promise.all(rest).then(function (pages) {
            var all = first.products.slice();

            pages.forEach(function (result) {
              all = all.concat(result.products);
            });

            return all;
          });
        })
        .catch(function (error) {
          // Reset the memo so a later retry can re-fetch.
          self._indexPromise = null;
          throw error;
        });

      return this._indexPromise;
    }

    _showResults() {
      var self = this;

      this._status.hidden = false;
      this._errorLink.hidden = true;
      this._statusText.textContent = this.dataset.labelLoading || "";
      this._nextButton.disabled = true;

      this._ensureIndex()
        .then(function (index) {
          var result = engine.filterIndex(
            index,
            self._selections,
            self._config,
            self._maxResults
          );
          var picked = result.products.map(function (product) {
            return {
              handle: product.handle,
              variantId: engine.pickVariant(
                product,
                self._selections,
                self._config
              ),
            };
          });

          document.dispatchEvent(
            new CustomEvent("rd:finder:complete", {
              detail: {
                answers: self._selections,
                resultCount: result.totalMatched,
                shownCount: picked.length,
              },
            })
          );

          // The compact string keeps the dataLayer payload flat and PII-free.
          self._track("finder_complete", {
            answers: self._buildCompact(),
            resultCount: result.totalMatched,
            shownCount: picked.length,
          });

          // view=produktvergleich forces the comparison template while the page's template suffix
          // is not assignable in the admin (the template only exists on the unpublished theme).
          window.location.assign(
            engine.buildResultsUrl(self.dataset.comparisonUrl, picked)
          );
        })
        .catch(function () {
          self._statusText.textContent = self.dataset.labelError || "";
          self._errorLink.hidden = false;
          self._nextButton.disabled = false;
        });
    }
  }

  window.customElements.define("product-finder", ProductFinder);
})();
